import { useRef, useState } from "react";

interface DropzoneProps {
  accept: string;
  multiple?: boolean;
  disabled?: boolean;
  files: File[];
  onFiles: (files: File[]) => void;
  hint?: string;
  variant?: "image" | "document";
}

function formatSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

function ImageIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <rect x="3" y="4" width="18" height="16" rx="1" />
      <circle cx="8.5" cy="9.5" r="1.5" />
      <path d="M21 16l-5-5-7 7" />
    </svg>
  );
}

function DocIcon() {
  return (
    <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.4">
      <path d="M13 3H7a1 1 0 0 0-1 1v16a1 1 0 0 0 1 1h10a1 1 0 0 0 1-1V8z" />
      <path d="M13 3v5h5" />
    </svg>
  );
}

export function Dropzone({
  accept,
  multiple = true,
  disabled = false,
  files,
  onFiles,
  hint,
  variant = "image",
}: DropzoneProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [dragging, setDragging] = useState(false);

  function matchesAccept(file: File): boolean {
    const rules = accept.split(",").map((r) => r.trim().toLowerCase());
    return rules.some((rule) => {
      if (rule === "" ) return true;
      if (rule.endsWith("/*")) return file.type.startsWith(rule.slice(0, -1));
      if (rule.startsWith(".")) return file.name.toLowerCase().endsWith(rule);
      return file.type === rule;
    });
  }

  function addFiles(incoming: File[]) {
    const valid = incoming.filter(matchesAccept);
    if (valid.length === 0) return;
    onFiles(multiple ? [...files, ...valid] : valid.slice(0, 1));
  }

  function removeAt(index: number) {
    onFiles(files.filter((_, i) => i !== index));
  }

  return (
    <div>
      <div
        role="button"
        tabIndex={disabled ? -1 : 0}
        onClick={() => !disabled && inputRef.current?.click()}
        onKeyDown={(e) => {
          if (!disabled && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            inputRef.current?.click();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragging(true);
        }}
        onDragLeave={(e) => {
          e.preventDefault();
          setDragging(false);
        }}
        onDrop={(e) => {
          e.preventDefault();
          setDragging(false);
          if (disabled) return;
          addFiles(Array.from(e.dataTransfer.files));
        }}
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 8,
          padding: "22px 16px",
          textAlign: "center",
          border: `1px dashed ${dragging ? "var(--accent)" : "var(--line)"}`,
          background: dragging ? "var(--surface-3)" : "var(--surface-2)",
          color: dragging ? "var(--accent)" : "var(--ink-mute)",
          cursor: disabled ? "not-allowed" : "pointer",
          opacity: disabled ? 0.5 : 1,
          transition: "background 0.15s, border-color 0.15s, color 0.15s",
        }}
      >
        <span
          style={{
            display: "grid",
            placeItems: "center",
            width: 40,
            height: 40,
            border: `1px solid ${dragging ? "var(--accent)" : "var(--line)"}`,
            color: dragging ? "var(--accent)" : "var(--ink-soft)",
            transform: dragging ? "translateY(-2px)" : "none",
            transition: "transform 0.15s, color 0.15s, border-color 0.15s",
          }}
        >
          {variant === "document" ? <DocIcon /> : <ImageIcon />}
        </span>
        <div
          style={{
            fontFamily: "var(--f-mono)",
            fontSize: 11,
            letterSpacing: "0.1em",
            textTransform: "uppercase",
          }}
        >
          {dragging
            ? "Drop to add"
            : `Drag & drop or click to browse`}
        </div>
        {hint && (
          <div
            style={{
              fontFamily: "var(--f-mono)",
              fontSize: 10,
              letterSpacing: "0.06em",
              color: "var(--ink-mute)",
            }}
          >
            {hint}
          </div>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          multiple={multiple}
          disabled={disabled}
          style={{ display: "none" }}
          onChange={(e) => {
            addFiles(Array.from(e.target.files ?? []));
            e.target.value = "";
          }}
        />
      </div>

      {files.length > 0 && (
        <div style={{ display: "flex", flexDirection: "column", gap: 6, marginTop: 8 }}>
          {files.map((file, i) => (
            <div
              key={`${file.name}-${i}`}
              style={{
                display: "flex",
                alignItems: "center",
                gap: 10,
                padding: "6px 10px",
                border: "1px solid var(--line)",
                background: "var(--surface)",
              }}
            >
              {variant === "image" ? (
                <img
                  src={URL.createObjectURL(file)}
                  alt=""
                  style={{
                    width: 32,
                    height: 32,
                    objectFit: "cover",
                    border: "1px solid var(--line)",
                    flexShrink: 0,
                  }}
                />
              ) : (
                <span style={{ color: "var(--ink-soft)", flexShrink: 0 }}>
                  <DocIcon />
                </span>
              )}
              <div style={{ minWidth: 0, flex: 1 }}>
                <div
                  style={{
                    fontSize: 12,
                    color: "var(--ink)",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {file.name}
                </div>
                <div
                  style={{
                    fontFamily: "var(--f-mono)",
                    fontSize: 10,
                    color: "var(--ink-mute)",
                  }}
                >
                  {formatSize(file.size)}
                </div>
              </div>
              <button
                type="button"
                onClick={() => removeAt(i)}
                disabled={disabled}
                style={{
                  background: "transparent",
                  border: 0,
                  color: "var(--ink-mute)",
                  cursor: disabled ? "not-allowed" : "pointer",
                  fontSize: 16,
                  lineHeight: 1,
                  padding: 4,
                  flexShrink: 0,
                }}
                aria-label={`Remove ${file.name}`}
              >
                ×
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
